import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { firestore, collection, addDoc, Timestamp } from '../../lib/firebase';
import codeslateIcon from '../../assets/codeslate-logo.png';
import './ContactPage.css';

interface SupportMessage {
  name: string;
  topic: string;
  message: string;
  createdAt: Timestamp;
}

interface ContactFormData {
  name: string;
  topic: string;
  message: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    topic: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      const supportMessage: Omit<SupportMessage, 'createdAt'> = {
        name: formData.name.trim(),
        topic: formData.topic.trim(),
        message: formData.message.trim(),
      };
      
      const supportRef = collection(firestore, 'support');
      await addDoc(supportRef, {
        ...supportMessage,
        createdAt: Timestamp.now()
      });
      
      setSubmitStatus('success');
      setFormData({ name: '', topic: '', message: '' });
      
    } catch (error) {
      console.error('Error submitting support message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Header */}
      <header className="contact-header">
        <div className="contact-nav">
          <Link to="/" className="contact-logo">
            <img src={codeslateIcon} alt="CodeSlate" className="logo-icon" />
            <span>CodeSlate</span>
          </Link>
          <Link to="/" className="back-to-home">
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="contact-main">
        <div className="contact-container">
          <h1 className="contact-title">Contact Us</h1>
          <p className="contact-subtitle">
            Have a question or need help? We'd love to hear from you.
          </p>

          <div className="contact-form-container">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="topic" className="form-label">
                  Topic *
                </label>
                <input
                  type="text"
                  id="topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="What's this about?"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Please describe your question or issue in detail..."
                  rows={6}
                  required
                />
              </div>

              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="success-message">
                  ✓ Message sent successfully! We'll get back to you soon.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="error-message">
                  ✗ Failed to send message. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
